import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { toAbsoluteUrl, usePossessiveName } from '@helpers/index';
import { formatMoney } from '@helpers/index';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';
import { formatCycleInterval } from '@modules/subscriptions/utils/formatCycle';

import {
  getCurrencyCodeSelector,
  getDiscountSelector,
  getLineItemSelector,
  getPetSelector,
} from '../../../core/selectors';
import { Recipe } from '../../../core/types';
import { getDisabledRecipes } from '../../../utils/getDisabledRecipes';
import { usePetProfileUpdateContext } from '../context';
import { CheckmarkIcon, ErrorIcon, LoadingSpinner, WarningIcon } from '../icons';
import { PENSION_INFO, RECIPE_INFO } from '../types';
import { findMatchingVariant, hasMatchingVariant, recipesMatch } from '../utils';

// "Frischfutter mit Rind, Frischfutter mit Huhn". Takes `intl` because it is
// called from the component body but is not a component itself.
const recipeNames = (intl: IntlShape, recipes: Recipe[]): string =>
  recipes
    .map((r) => {
      const meatId = RECIPE_INFO[r]?.meatId;
      if (!meatId) return r;
      return intl.formatMessage(
        { id: 'SUBSCRIPTION.FEEDING.FRESH_WITH' },
        { meat: intl.formatMessage({ id: meatId }) },
      );
    })
    .join(', ') || '—';

/** Flavor chips (icon + brand name): Cowgenuss, Gockelspeis, Ringeltanz. */
const RecipeChips: React.FC<{ recipes: Recipe[]; align: 'left' | 'right' }> = ({
  recipes,
  align,
}) => {
  if (recipes.length === 0) return <span className="rud-value__muted">—</span>;
  return (
    <div className={`rud-chips ${align === 'right' ? 'rud-chips--right' : ''}`}>
      {recipes.map((r) => {
        const info = RECIPE_INFO[r];
        return (
          <span key={r} className={`rud-chip rud-chip--${r}`}>
            {info?.icon && <img className="rud-chip__icon" src={toAbsoluteUrl(info.icon)} alt="" />}
            {info?.name ?? r}
          </span>
        );
      })}
    </div>
  );
};

interface DiffRowProps {
  label: string;
  oldValue: React.ReactNode;
  newValue: React.ReactNode;
  changed?: boolean;
  emphasis?: boolean;
}

const DiffRow: React.FC<DiffRowProps> = ({ label, oldValue, newValue, changed, emphasis }) => (
  <div className={`rud-row ${emphasis ? 'rud-row--emphasis' : ''}`}>
    <span className="rud-row__label">{label}</span>
    <div className="rud-row__values">
      <div className={`rud-value rud-value--old ${changed ? 'ppu-change-item__old' : ''}`}>
        {oldValue}
      </div>
      <span className="rud-arrow ppu-change-item__arrow">→</span>
      <div className={`rud-value rud-value--new ${changed ? 'ppu-change-item__new' : ''}`}>
        {newValue}
      </div>
    </div>
  </div>
);

/**
 * Read-only before/after comparison for the "update-required" recalculation
 * flow. Selections are kept as-is; in practice only calories and price change.
 *
 * Edge cases (typically tiny dogs whose recalculated calorie need maps to a
 * smaller product catalogue): the recalculated product may not offer the
 * customer's current Halbpension, 14-day cycle, or a recipe (allergy). We keep
 * the flow completable by falling back to the closest available variant and
 * clearly explaining every forced adjustment.
 */
export const RecipeUpdateDiff: React.FC = () => {
  const intl = useIntl();
  const {
    recalculatedProduct,
    selectedVariant,
    customization,
    setCustomization,
    currentOptions,
    isRecalculating,
    isUpdating,
    handleSubmit,
  } = usePetProfileUpdateContext();

  const pet = useSelector(getPetSelector);
  const lineItem = useSelector(getLineItemSelector);
  const discount = useSelector(getDiscountSelector);
  const currencyCode = useSelector(getCurrencyCodeSelector);

  const variants = recalculatedProduct?.product.variants;
  // Both consuming messages read "für {name}", which is accusative in German.
  // The fallback was "deinem Hund" (dative), so a nameless pet produced
  // "für deinem Hund".
  const petName = pet?.name || intl.formatMessage({ id: 'GLOBAL.YOUR_DOG.ACC' });
  // Before the `isRecalculating` early return: hooks must run on every render.
  const possessiveName = usePossessiveName(pet?.name);

  // Recipes disabled for the recalculated product (allergy + API restrictions).
  const disabledRecipes = useMemo(() => {
    const allergyDisabled = pet ? getDisabledRecipes(pet) : [];
    const recalculatedDisabled = recalculatedProduct?.restrictions.disabledRecipes || [];
    return Array.from(new Set([...allergyDisabled, ...recalculatedDisabled]));
  }, [pet, recalculatedProduct]);

  // Which option groups simply don't exist in the recalculated product.
  const noHalbpension = useMemo(
    () => !!variants?.length && !variants.some((v) => v.selectedOptions?.pension === 'H'),
    [variants],
  );
  const no14DayCycle = useMemo(
    () => !!variants?.length && !variants.some((v) => v.selectedOptions?.cycle === 14),
    [variants],
  );

  // Keep the customer's selection where possible; otherwise fall back to the
  // closest available variant (same recipes → same cycle → any).
  useEffect(() => {
    if (!recalculatedProduct || !variants) return;

    const { selectedRecipes, pension, cycle } = customization;
    if (hasMatchingVariant(variants, { recipes: selectedRecipes, pension, cycle })) return;

    const sameCycle = findMatchingVariant(variants, { recipes: selectedRecipes, cycle });
    if (sameCycle) {
      setCustomization((prev) => ({
        ...prev,
        pension: sameCycle.selectedOptions.pension,
      }));
      return;
    }

    const any = findMatchingVariant(variants, { recipes: selectedRecipes });
    if (any) {
      setCustomization((prev) => ({
        ...prev,
        pension: any.selectedOptions.pension,
        cycle: any.selectedOptions.cycle,
      }));
    }
  }, [
    customization.selectedRecipes,
    customization.pension,
    customization.cycle,
    recalculatedProduct,
    variants,
    setCustomization,
  ]);

  const newKcal = recalculatedProduct?.calculatedCalories.kcal || 0;
  const oldKcal = lineItem?.product?.calories?.kcal
    ? typeof lineItem.product.calories.kcal === 'string'
      ? parseInt(lineItem.product.calories.kcal, 10)
      : lineItem.product.calories.kcal
    : 0;

  // Price: keep the existing discount and apply it to the recalculated base price.
  const newBasePrice = selectedVariant ? parseFloat(String(selectedVariant.price || '0')) : 0;
  const oldBasePrice = lineItem?.basePrice || 0;
  const oldDiscountedPrice = lineItem?.discountedPrice || oldBasePrice;

  const discountPercentage = useMemo<number | null>(() => {
    if (oldBasePrice > 0 && oldDiscountedPrice < oldBasePrice) {
      return ((oldBasePrice - oldDiscountedPrice) / oldBasePrice) * 100;
    }
    if (
      discount?.value &&
      typeof discount.value === 'object' &&
      'percentage' in discount.value &&
      typeof discount.value.percentage === 'number'
    ) {
      return discount.value.percentage;
    }
    return null;
  }, [oldBasePrice, oldDiscountedPrice, discount]);

  const newPrice =
    discountPercentage && discountPercentage > 0
      ? newBasePrice - (newBasePrice * discountPercentage) / 100
      : newBasePrice;
  const oldPrice = oldDiscountedPrice;

  // Differences between the customer's current setup and the new one.
  const recipesChanged = !recipesMatch(
    currentOptions.selectedRecipes,
    customization.selectedRecipes,
  );
  const pensionChanged = currentOptions.pension !== customization.pension;
  const cycleChanged = currentOptions.cycle !== customization.cycle;
  const caloriesChanged = newKcal !== oldKcal && oldKcal > 0;
  const priceChanged = Math.abs(newPrice - oldPrice) > 0.01 && oldPrice > 0;

  const droppedRecipes = currentOptions.selectedRecipes.filter(
    (r) => !customization.selectedRecipes.includes(r),
  );

  // A selected recipe that is still disabled means we cannot offer a valid plan.
  const hasBlockingRecipe = customization.selectedRecipes.some((r) => disabledRecipes.includes(r));

  // Plain-language explanations for every forced adjustment.
  const notes = useMemo(() => {
    const out: { id: string; text: string }[] = [];

    if (droppedRecipes.length > 0) {
      out.push({
        id: 'recipes',
        text: intl.formatMessage(
          { id: 'PET_PROFILE.RECIPE_DIFF.NOTE.RECIPE_DROPPED' },
          { recipes: recipeNames(intl, droppedRecipes), name: petName },
        ),
      });
    }
    if (currentOptions.pension === 'H' && noHalbpension) {
      out.push({
        id: 'pension',
        text: intl.formatMessage(
          { id: 'PET_PROFILE.RECIPE_DIFF.NOTE.NO_HALF_PENSION' },
          { kcal: newKcal },
        ),
      });
    }
    if (currentOptions.cycle === 14 && no14DayCycle) {
      out.push({
        id: 'cycle',
        text: intl.formatMessage({ id: 'PET_PROFILE.RECIPE_DIFF.NOTE.NO_14_DAY_CYCLE' }),
      });
    }
    return out;
  }, [
    droppedRecipes,
    currentOptions.pension,
    currentOptions.cycle,
    noHalbpension,
    no14DayCycle,
    newKcal,
    petName,
    intl,
  ]);

  if (isRecalculating) {
    return (
      <div className="rud">
        <div className="rud__loading">
          <div className="rud__loading-spinner">
            <LoadingSpinner />
          </div>
          <p className="rud__loading-title">
            <FormattedMessage id="PET_PROFILE.RECALCULATING" />
          </p>
          <p className="rud__loading-subtitle">
            <FormattedMessage id="GLOBAL.PLEASE_WAIT" />
          </p>
        </div>
      </div>
    );
  }

  const canSubmit = !!selectedVariant && !hasBlockingRecipe;

  return (
    <div className="rud">
      <div className="rud__header">
        <h3 className="rud__title">
          <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.TITLE" values={{ name: possessiveName }} />
        </h3>
        <p className="rud__subtitle">
          <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.SUBTITLE" />
        </p>
      </div>

      <div className="rud__card">
        <div className="rud__columns">
          <span className="rud__col rud__col--old">
            <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.COL_CURRENT" />
          </span>
          <span className="rud__col-spacer" />
          <span className="rud__col rud__col--new">
            <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.COL_NEW" />
          </span>
        </div>

        <DiffRow
          label={intl.formatMessage({ id: 'PET_PROFILE.LABEL.RECIPES' })}
          changed={recipesChanged}
          oldValue={<RecipeChips recipes={currentOptions.selectedRecipes} align="right" />}
          newValue={<RecipeChips recipes={customization.selectedRecipes} align="left" />}
        />
        <DiffRow
          label={intl.formatMessage({ id: 'PET_PROFILE.LABEL.CALORIE_NEED' })}
          emphasis
          changed={caloriesChanged}
          oldValue={
            oldKcal > 0 ? intl.formatMessage({ id: 'KCAL.PER_DAY' }, { kcal: oldKcal }) : '—'
          }
          newValue={intl.formatMessage({ id: 'KCAL.PER_DAY' }, { kcal: newKcal })}
        />
        <DiffRow
          label={intl.formatMessage({ id: 'SUBSCRIPTION.PORTION_SIZE' })}
          changed={pensionChanged}
          oldValue={intl.formatMessage({ id: PENSION_INFO[currentOptions.pension].labelId })}
          newValue={intl.formatMessage({ id: PENSION_INFO[customization.pension].labelId })}
        />
        <DiffRow
          label={intl.formatMessage({ id: 'SUBSCRIPTION.UPDATE.LABEL_DELIVERY_PLAN' })}
          changed={cycleChanged}
          oldValue={formatCycleInterval(intl, currentOptions.cycle)}
          newValue={formatCycleInterval(intl, customization.cycle)}
        />

        <DiffRow
          label={intl.formatMessage({ id: 'PET_PROFILE.LABEL.PRICE_PER_DELIVERY' })}
          emphasis
          changed={priceChanged}
          oldValue={oldPrice > 0 ? formatMoney(oldPrice, currencyCode, intl.locale) : '—'}
          newValue={formatMoney(newPrice, currencyCode, intl.locale)}
        />

        {discount && (
          <p className="rud__discount-note">
            <FormattedMessage
              id="DISCOUNT.STILL_APPLIED"
              values={{
                label:
                  formatDiscountTitle(intl, discount.title) ||
                  intl.formatMessage({ id: 'DISCOUNT.FALLBACK' }),
              }}
            />
          </p>
        )}
      </div>

      {/* Forced-adjustment explanations (Halbpension / Lieferplan / Rezept) */}
      {canSubmit && notes.length > 0 && (
        <div className="rud__notice">
          <span className="rud__notice-icon">
            <WarningIcon />
          </span>
          <div className="rud__notice-content">
            <p className="rud__notice-title">
              <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.ADJUSTED_TITLE" />
            </p>
            {notes.map((n) => (
              <p key={n.id} className="rud__notice-text">
                {n.text}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* No valid plan for the kept selection — direct to support. */}
      {!canSubmit && (
        <div className="rud__notice rud__notice--error">
          <span className="rud__notice-icon">
            <ErrorIcon />
          </span>
          <div className="rud__notice-content">
            <p className="rud__notice-title">
              <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.UNAVAILABLE_TITLE" />
            </p>
            <p className="rud__notice-text">
              {hasBlockingRecipe
                ? intl.formatMessage(
                    { id: 'PET_PROFILE.RECIPE_DIFF.UNAVAILABLE_RECIPE' },
                    { name: petName },
                  )
                : intl.formatMessage({ id: 'PET_PROFILE.RECIPE_DIFF.UNAVAILABLE_PRODUCT' })}
            </p>
          </div>
        </div>
      )}

      <div className="rud__actions">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isUpdating}
          className={`rud__submit ${isUpdating ? 'rud__submit--loading' : ''}`}>
          {isUpdating ? (
            <>
              <LoadingSpinner />
              <FormattedMessage id="GLOBAL.SAVING" />
            </>
          ) : (
            <>
              <FormattedMessage id="PET_PROFILE.UPDATE_SUBSCRIPTION" />
              <CheckmarkIcon />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
